import { ReactElement, useState } from 'react';
import {
  Button,
  ContextMenu,
  essentialsFeatures,
  Pill,
  PricingSlider,
  PRICING_SLIDER_CONTACT_INDEX,
  scaleList,
} from '@sc/shared-ui';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ESSENTIALS_STEPS, stepIndexForSubtotal } from '@sc/stripe/pricing';
import { SubscriptionType } from '@sc/graphql';
import { useNavigate } from '@tanstack/react-router';
import { useUser } from '../providers/users';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { UpgradeCheckoutModal } from '../modals/UpgradeCheckoutModal';

const FOOTNOTES = [
  'A query allows up to 50,000 tokens; richer queries can be configured per knowledge base and consume 2x, 3x etc. A page covers approximately one page of standard text content; dense or complex documents and imported web content may use more than one page.',
  'Subscription queries and pages reset monthly and do not roll over.',
  'Running out of queries pauses any further messages. Running out of pages pauses new imports.',
];

const PlanBullets = ({ bullets }: { bullets: string[] }): ReactElement => (
  <ul className="flex flex-col gap-3 items-start w-full" role="list">
    {bullets.map((item) => (
      <li className="flex gap-2 items-start" key={item}>
        <CheckCircleIcon
          className="mt-1 w-5 h-5 text-grey-400 shrink-0"
          aria-hidden="true"
        />
        <p className="font-grotesque text-lg leading-6.5 font-semibold text-white">
          {item}
        </p>
      </li>
    ))}
  </ul>
);

export const Upgrade = (): ReactElement => {
  const { user } = useUser();
  const navigate = useNavigate();

  const subscription = user.company.subscription;
  const currentType = subscription?.type ?? null;
  const isEnterprise = currentType === SubscriptionType.enterprise;

  // Legacy subtotals (pre-slider plans) have no matching step; start those —
  // and new customers — at the entry step.
  const currentStepIndex = subscription
    ? stepIndexForSubtotal(subscription.subtotal)
    : null;

  const [stepIndex, setStepIndex] = useState(currentStepIndex ?? 0);

  const { manageOptions, confirmAction, isPaused, isExpiring, addCardModal } =
    useSubscriptionActions();

  const [showCheckout, setShowCheckout] = useState(false);

  const isContactStop = stepIndex >= PRICING_SLIDER_CONTACT_INDEX;
  const selectedStep = isContactStop ? undefined : ESSENTIALS_STEPS[stepIndex];
  const isCurrentStep =
    currentStepIndex !== null && stepIndex === currentStepIndex;

  // Downgrades don't exist as a flow — the upgrade credit maths assumes the
  // new plan costs more than the allowance it replaces.
  const isDowngrade =
    !!subscription &&
    !isCurrentStep &&
    !!selectedStep &&
    selectedStep.priceGbp <= subscription.subtotal;

  // Status pill on the current plan card.
  const statusBadge = isExpiring ? (
    <Pill
      size="xl"
      colour="#da892b"
      backgroundColour="#241a0f"
      outline
      text="Cancelling"
    />
  ) : isPaused ? (
    <Pill
      size="xl"
      colour="#b8c5d0"
      backgroundColour="#1c2227"
      outline
      text="Paused"
    />
  ) : undefined;

  const essentialsButtonText = !subscription
    ? 'Subscribe'
    : isCurrentStep
      ? 'Current plan'
      : 'Pay now';

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-5 flex flex-col gap-1">
        <Breadcrumbs items={[{ link: { to: '/billing' }, name: 'Billing' }]} />
        <h1 className="text-h1-small font-semibold font-grotesque">
          Pricing plans
        </h1>
      </div>
      <div className="px-5 pb-5 flex flex-col lg:flex-row gap-5 items-stretch">
        <div className="bg-background-tertiary flex flex-col rounded-xs overflow-hidden flex-1 max-w-180">
          {currentType === SubscriptionType.essentials && (
            <div className="bg-primary-1000 text-primary-400 text-center font-grotesque font-semibold text-sm uppercase tracking-wide py-2">
              Current plan
            </div>
          )}
          <div className="flex justify-between items-center gap-1 p-5 border-b border-grey-t-700">
            <div>
              <h3 className="font-grotesque text-2xl font-medium text-white">
                Essentials
              </h3>
              <p className="text-sm text-text-low-priority">
                Scale your monthly usage to fit
              </p>
            </div>
            {statusBadge}
          </div>
          <div className="p-5 space-y-5 grow">
            <PricingSlider stepIndex={stepIndex} onChange={setStepIndex} />
            {isDowngrade && (
              <p className="text-sm text-text-low-priority">
                Downgrades aren&apos;t available — pick a larger plan, or
                contact us if you need to reduce your usage.
              </p>
            )}
            {/* Existing subscribers already have the features and see the
                exact charge in the checkout summary. */}
            {!subscription && <PlanBullets bullets={essentialsFeatures} />}
          </div>
          <div className="flex gap-4 items-center w-full p-5 border-t border-grey-t-700">
            {isEnterprise ? (
              <p className="text-sm text-text-low-priority">
                Plan changes on Scale are managed through your account manager.
              </p>
            ) : (
              <>
                {isContactStop ? (
                  <Button
                    button={{ text: 'Contact us', size: 'xl' }}
                    link={{
                      href: 'https://starkchat.com/contact',
                      target: '_blank',
                    }}
                  />
                ) : (
                  <Button
                    button={{
                      text: essentialsButtonText,
                      size: 'xl',
                      disabled: isCurrentStep || isDowngrade,
                      onClick: () => setShowCheckout(true),
                    }}
                  />
                )}
                {currentType === SubscriptionType.essentials &&
                  manageOptions.length > 0 && (
                    <ContextMenu
                      type="basic"
                      size="large"
                      label="Actions"
                      options={manageOptions}
                      onOptionSelect={confirmAction}
                    />
                  )}
              </>
            )}
          </div>
        </div>
        <div className="bg-background-tertiary flex flex-col rounded-xs overflow-hidden flex-1 max-w-120">
          {isEnterprise && (
            <div className="bg-primary-1000 text-primary-400 text-center font-grotesque font-semibold text-sm uppercase tracking-wide py-2">
              Current plan
            </div>
          )}
          <div className="flex justify-between items-center gap-1 p-5 border-b border-grey-t-700">
            <div>
              <h3 className="font-grotesque text-2xl font-medium text-white">
                Scale
              </h3>
              <p className="text-sm text-text-low-priority">
                For teams building chat into their own apps
              </p>
            </div>
          </div>
          <div className="p-5 space-y-5 grow">
            <p className="font-grotesque text-4xl font-normal text-white">
              Custom pricing
            </p>
            <PlanBullets bullets={scaleList} />
          </div>
          <div className="flex flex-col gap-4 items-start w-full p-5 border-t border-grey-t-700">
            {isEnterprise ? (
              <Button
                button={{
                  text: 'Current plan',
                  size: 'xl',
                  type: 'low-priority',
                  disabled: true,
                }}
              />
            ) : (
              <Button
                button={{ text: 'Contact us', size: 'xl', type: 'secondary' }}
                link={{
                  href: 'https://starkchat.com/contact',
                  target: '_blank',
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="px-5 pb-10 max-w-300">
        <ul className="space-y-1 text-xs text-text-low-priority">
          {FOOTNOTES.map((note) => (
            <li key={note}>
              <span>{note}</span>
            </li>
          ))}
          <li>
            <a
              href="https://starkchat.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white"
            >
              Full terms available
            </a>
          </li>
        </ul>
      </div>
      <UpgradeCheckoutModal
        open={showCheckout}
        priceStep={stepIndex}
        onClose={() => setShowCheckout(false)}
        onSuccess={() => void navigate({ to: '/billing' })}
      />
      {addCardModal}
    </div>
  );
};
